import type { EnvironmentId, ProjectId } from "@afar-research/contracts";
import { squashAtomCommandFailure } from "@afar-research/client-runtime/state/runtime";
import { buildTemporaryWorktreeBranchName } from "@afar-research/shared/git";
import { CheckIcon, FolderOpenIcon } from "lucide-react";
import { useState } from "react";

import { type DraftId, type DraftThreadState, useComposerDraftStore } from "../composerDraftStore";
import { readLocalApi } from "../localApi";
import { refreshWorktreeInventories } from "../lib/worktreeInventoryState";
import { randomHex } from "../lib/utils";
import { useAtomCommand } from "../state/use-atom-command";
import { worktreeEnvironment } from "../state/worktrees";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { commandFailureMessage } from "~/rpc/transportError";

export function BranchToolbarWorktreeSelector(props: {
  readonly environmentId: EnvironmentId;
  readonly projectId: ProjectId;
  readonly draftTarget: DraftId;
  readonly draftThread: DraftThreadState;
  readonly disabled: boolean;
}) {
  const setDraftThreadContext = useComposerDraftStore((store) => store.setDraftThreadContext);
  const createWorktree = useAtomCommand(worktreeEnvironment.create, {
    reportFailure: false,
  });
  const location = props.draftThread.worktreeLocation;
  const [newWorktreeDialogOpen, setNewWorktreeDialogOpen] = useState(
    props.draftThread.worktreePath === null,
  );
  const [worktreeName, setWorktreeName] = useState("");
  const [parentPath, setParentPath] = useState(
    location.type === "custom" ? location.parentPath : "",
  );
  const [useProjectLocation, setUseProjectLocation] = useState(location.type === "project");
  const [isPickingLocation, setIsPickingLocation] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [creationError, setCreationError] = useState<string | null>(null);
  const trimmedParentPath = parentPath.trim();
  const pendingLocation = useProjectLocation
    ? ({ type: "project" } as const)
    : trimmedParentPath.length > 0
      ? ({ type: "custom", parentPath: trimmedParentPath } as const)
      : ({ type: "host-store" } as const);
  const trimmedName = worktreeName.trim();
  const creationDisabled =
    props.disabled ||
    isCreating ||
    isPickingLocation ||
    trimmedName.length === 0 ||
    trimmedName.length > 80 ||
    props.draftThread.branch === null;

  const cancelNewWorktree = () => {
    if (isCreating) return;
    setNewWorktreeDialogOpen(false);
    setDraftThreadContext(props.draftTarget, { envMode: "local" });
  };

  const confirmNewWorktree = async () => {
    const baseBranch = props.draftThread.branch;
    if (creationDisabled || baseBranch === null) return;
    setIsCreating(true);
    setCreationError(null);
    try {
      const result = await createWorktree({
        environmentId: props.environmentId,
        input: {
          projectId: props.projectId,
          ownerThreadId: props.draftThread.threadId,
          name: trimmedName,
          baseBranch,
          branch: buildTemporaryWorktreeBranchName(randomHex),
          location: pendingLocation,
          startFromOrigin: props.draftThread.startFromOrigin,
        },
      });
      if (result._tag === "Failure") {
        const error = squashAtomCommandFailure(result);
        setCreationError(commandFailureMessage(error, "Worktree creation failed."));
        return;
      }
      refreshWorktreeInventories(props.environmentId, [props.projectId]);
      setDraftThreadContext(props.draftTarget, {
        worktreePath: result.value.path,
        branch: result.value.branch,
        envMode: "worktree",
        worktreeLocation: pendingLocation,
      });
      setNewWorktreeDialogOpen(false);
    } finally {
      setIsCreating(false);
    }
  };

  const pickParentDirectory = async () => {
    if (props.disabled || isCreating || isPickingLocation) return;
    const api = readLocalApi();
    if (!api) return;
    setIsPickingLocation(true);
    setCreationError(null);
    try {
      const selectedPath = await api.dialogs.pickFolder(
        trimmedParentPath.length > 0 ? { initialPath: trimmedParentPath } : undefined,
      );
      if (!selectedPath) return;
      setParentPath(selectedPath);
      setUseProjectLocation(false);
    } catch (error) {
      setCreationError(commandFailureMessage(error, "The folder picker could not be opened."));
    } finally {
      setIsPickingLocation(false);
    }
  };

  return (
    <>
      <Dialog
        open={newWorktreeDialogOpen}
        onOpenChange={(open) => {
          if (open) {
            setParentPath(location.type === "custom" ? location.parentPath : "");
            setUseProjectLocation(location.type === "project");
            setCreationError(null);
            setNewWorktreeDialogOpen(true);
          } else if (!isCreating) {
            cancelNewWorktree();
          }
        }}
      >
        <DialogPopup className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create new worktree</DialogTitle>
            <DialogDescription>
              Name this thread&apos;s isolated worktree and choose where to create it now.
            </DialogDescription>
          </DialogHeader>
          <DialogPanel className="space-y-4">
            <label className="grid gap-1.5">
              <span className="text-sm font-medium">Worktree name</span>
              <Input
                autoFocus
                aria-label="Worktree name"
                disabled={props.disabled || isCreating}
                maxLength={80}
                placeholder="e.g. Authentication redesign"
                value={worktreeName}
                onChange={(event) => setWorktreeName(event.target.value)}
              />
            </label>
            <div className="grid gap-1.5">
              <span className="text-sm font-medium">Location</span>
              <div className="flex gap-2">
                <Input
                  aria-label="Worktree parent directory"
                  placeholder="Default worktree path"
                  value={parentPath}
                  disabled={props.disabled || isCreating || isPickingLocation || useProjectLocation}
                  onChange={(event) => {
                    setParentPath(event.target.value);
                    setUseProjectLocation(false);
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={props.disabled || isCreating || isPickingLocation || useProjectLocation}
                  onClick={() => void pickParentDirectory()}
                >
                  <FolderOpenIcon className="size-3.5" />
                  {isPickingLocation ? "Opening…" : "Browse"}
                </Button>
              </div>
              <div className="grid gap-2">
                <Button
                  type="button"
                  className="w-full justify-start"
                  variant={useProjectLocation ? "default" : "outline"}
                  aria-pressed={useProjectLocation}
                  disabled={props.disabled || isCreating || isPickingLocation}
                  onClick={() => setUseProjectLocation((selected) => !selected)}
                >
                  {useProjectLocation ? <CheckIcon className="size-3.5" /> : null}
                  {useProjectLocation ? "Using project level" : "Use project level"}
                </Button>
                <span className="text-muted-foreground text-xs">
                  {useProjectLocation
                    ? "The worktree will be created inside this project's .afar-research/worktrees directory."
                    : "Leave blank for the default. A selected folder becomes the custom parent."}
                </span>
              </div>
            </div>
            {creationError ? (
              <p className="text-destructive text-sm" role="alert">
                {creationError}
              </p>
            ) : null}
          </DialogPanel>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isCreating}
              onClick={cancelNewWorktree}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={creationDisabled}
              onClick={() => void confirmNewWorktree()}
            >
              {isCreating ? "Creating worktree…" : "Use new worktree"}
            </Button>
          </DialogFooter>
        </DialogPopup>
      </Dialog>
    </>
  );
}
